// src/features/subscription/format.ts
// Shared formatting helpers for the subscription catalog pages (plans,
// add-ons, bundles, discounts). Mirrors src/features/billing/format.ts and
// src/features/payment/format.ts — the currency-aware display precedent
// named in the forms-wave mandate. Unlike billing's nested `Money` message,
// these domain models carry a flat `amount: number` (minor units) plus a
// separate `currency` enum field, so formatMoney takes them separately.

/** amountMinor is in minor units (cents); currency is the Currency enum, e.g. 'CURRENCY_USD'. */
export function formatMoney(amountMinor: number | null | undefined, currency?: string | null): string {
  if (amountMinor == null) return '--';
  const amount = amountMinor / 100;
  const formatted = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const code = humanizeEnum(currency, 'CURRENCY_');
  return code ? `${formatted} ${code}` : formatted;
}

export function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(dateStr);
  }
}

/** Turns 'CURRENCY_USD' into 'USD', 'BILLING_CYCLE_MONTHLY' into 'Monthly'. */
export function humanizeEnum(value: string | undefined | null, stripPrefix?: string): string {
  if (!value) return '';
  let v = value;
  if (stripPrefix && v.startsWith(stripPrefix)) v = v.slice(stripPrefix.length);
  if (v === 'UNSPECIFIED') return '';
  if (stripPrefix === 'CURRENCY_') return v; // keep currency codes upper-case (USD, EUR, ...)
  return v
    .toLowerCase()
    .split('_')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/**
 * Major-units text ("9.99") entered by the person -> minor-units integer
 * (999) sent on the wire. Empty/invalid input -> 0, same convention as
 * GrantCreditModal (src/features/billing/GrantCreditModal.tsx).
 */
export function toMinorUnits(majorText: string): number {
  const n = parseFloat(majorText);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/**
 * Minor-units integer (999) from the wire -> major-units text ("9.99") for a
 * clearable text input. Zero is rendered as empty (unset), same convention
 * used for numeric fields across the form kit (e.g. RoleFormDialog priority).
 */
export function toMajorUnitsText(amountMinor: number | null | undefined): string {
  if (!amountMinor) return '';
  return (amountMinor / 100).toFixed(2);
}

export function errMessage(e: unknown, fallback: string): string {
  const anyErr = e as any;
  return anyErr?.message || anyErr?.error?.message || fallback;
}
